// Authenticated location sync goes through /api/user/location → user_profiles.
// localStorage remains the anonymous / offline fallback.
// Function names keep the historical *Firestore suffix for call-site compatibility.

import type { RegionPrefSource, RegionPriority } from './subregion-pref';

const STORAGE_KEY_PREFIX = 'user-location-preference-';

/**
 * Window event fired whenever the shopping region changes in THIS tab.
 *
 * The `storage` event only fires in *other* tabs, so anything that renders the
 * current location (the header region badge, the language menu footer) would
 * stay stale in the tab that made the change. Every write path should announce
 * itself with `dispatchRegionSelectionChanged()`; `setUserLocation` already
 * does, so most callers get it for free.
 *
 * Listeners must re-read the location themselves — the event carries no
 * payload on purpose, so there is one source of truth (localStorage) and no
 * risk of a listener trusting a stale detail object.
 */
export const REGION_SELECTION_CHANGED_EVENT = 'region-selection-changed';

/** Announce a region change to same-tab listeners. Safe to call on the server (no-op). */
export function dispatchRegionSelectionChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(REGION_SELECTION_CHANGED_EVENT));
  } catch (error) {
    // A missing Event constructor must never break a location write.
    console.error('Error dispatching region selection change:', error);
  }
}

export interface UserLocation {
  city: string;           // User-selected city (for display)
  region: string;         // Province/state/region (for prompts)
  country: string;        // Country code from locale
  coordinates?: { lat: number; lng: number };
  updatedAt: number;
  /**
   * Which ordering the shopper wants: their subregion's results first, or the
   * whole country's. ADDITIVE — every location stored before this field existed
   * parses unchanged, and `undefined` means `country-first`, which is exactly
   * today's behavior. Nothing reads it for a decision until a later flag does.
   */
  regionPriority?: RegionPriority;
  /**
   * Whether the shopper stated this region or we inferred it. Absent means
   * unknown provenance, which is treated as `detected` — never as a statement
   * the shopper did not make.
   */
  regionSource?: RegionPrefSource;
}

/**
 * Get country-specific storage key
 */
function getStorageKey(countryCode: string): string {
  return `${STORAGE_KEY_PREFIX}${countryCode}`;
}

/**
 * Get user location from localStorage for a specific country
 * @param countryCode - The country code (e.g., 'CA', 'US', 'FR')
 */
export function getUserLocation(countryCode?: string): UserLocation | null {
  if (typeof window === 'undefined') return null;
  
  // If no country code provided, try to get it from the old storage key
  // This is for backward compatibility - but we CANNOT use it directly
  // because it might be for a different country than the current site
  if (!countryCode) {
    try {
      const oldStored = localStorage.getItem('user-location-preference');
      if (oldStored) {
        const location: UserLocation = JSON.parse(oldStored);
        // CRITICAL: Don't return the location without country validation
        // The caller MUST provide a countryCode to ensure country-specific filtering
        console.warn('getUserLocation called without countryCode - this is deprecated. Please provide countryCode parameter.');
        return location;
      }
    } catch (error) {
      // Ignore errors from old storage
    }
    return null;
  }
  
  try {
    // First, try the country-specific storage
    const stored = localStorage.getItem(getStorageKey(countryCode));
    if (stored) {
      const location: UserLocation = JSON.parse(stored);
      
      // Validate that the stored location matches the requested country
      if (location.country !== countryCode) {
        console.warn(`Stored location country (${location.country}) doesn't match requested country (${countryCode})`);
        return null;
      }
      
      return location;
    }
    
    // If no country-specific storage exists, check old storage for migration
    const oldStored = localStorage.getItem('user-location-preference');
    if (oldStored) {
      const oldLocation: UserLocation = JSON.parse(oldStored);
      
      // CRITICAL: Only return old location if it matches the current country
      if (oldLocation.country === countryCode) {
        // Migrate to new storage format automatically
        setUserLocation(oldLocation);
        return oldLocation;
      } else {
        // Old location is for a different country - don't return it
        console.log(`Old location is for ${oldLocation.country}, but current site is ${countryCode} - ignoring`);
        return null;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error reading user location from localStorage:', error);
    return null;
  }
}

/**
 * Save user location to localStorage for a specific country
 */
export function setUserLocation(location: UserLocation): void {
  if (typeof window === 'undefined') return;
  
  try {
    const locationWithTimestamp: UserLocation = {
      ...location,
      updatedAt: Date.now(),
    };
    localStorage.setItem(getStorageKey(location.country), JSON.stringify(locationWithTimestamp));

    // Clean up old non-country-specific storage
    localStorage.removeItem('user-location-preference');
  } catch (error) {
    console.error('Error saving user location to localStorage:', error);
  }

  // Outside the try: a quota failure above must not silently skip the notify,
  // and a listener throwing must not look like a failed write.
  dispatchRegionSelectionChanged();
}

/**
 * Get just the region string for use in AI prompts
 * @param countryCode - The country code to get the region for
 */
export function getRegionForPrompt(countryCode?: string): string | null {
  const location = getUserLocation(countryCode);
  return location?.region || null;
}

async function getIdTokenForUser(userId: string): Promise<string | null> {
  const { getAuthInstance } = await import('./firebase');
  const auth = getAuthInstance();
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.uid !== userId) return null;
  return currentUser.getIdToken();
}

/**
 * Load user location from Supabase user_profiles for logged-in users.
 * Locations are stored in preferences.locations keyed by country code.
 */
export async function loadLocationFromFirestore(userId: string, countryCode?: string): Promise<UserLocation | null> {
  try {
    const token = await getIdTokenForUser(userId);
    if (!token) return null;

    const qs = countryCode ? `?country=${encodeURIComponent(countryCode)}` : '';
    const res = await fetch(`/api/user/location${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const json = await res.json();
    return (json.location as UserLocation) || null;
  } catch (error) {
    console.error('Error loading location from user_profiles:', error);
    return null;
  }
}

/**
 * Sync user location to Supabase user_profiles for logged-in users.
 * Stores locations in preferences.locations keyed by country code.
 */
export async function syncLocationToFirestore(userId: string, location?: UserLocation): Promise<void> {
  try {
    const locationToSync = location || getUserLocation();
    if (!locationToSync) {
      console.log('No location to sync to user_profiles');
      return;
    }

    const token = await getIdTokenForUser(userId);
    if (!token) {
      console.warn('User not authenticated or userId mismatch, skipping location sync');
      return;
    }

    const res = await fetch('/api/user/location', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ location: locationToSync }),
    });
    if (!res.ok) {
      console.warn('Location sync failed with status', res.status);
      return;
    }
    console.log('Location synced to user_profiles for country:', locationToSync.country);
  } catch (error) {
    console.error('Error syncing location to user_profiles:', error);
  }
}

/**
 * Clear user location from localStorage for a specific country or all countries
 * @param countryCode - Optional country code. If not provided, clears all locations
 */
export function clearUserLocation(countryCode?: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    if (countryCode) {
      localStorage.removeItem(getStorageKey(countryCode));
    } else {
      // Clear all country-specific locations
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith(STORAGE_KEY_PREFIX)
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Also clear old non-country-specific key
      localStorage.removeItem('user-location-preference');
    }
  } catch (error) {
    console.error('Error clearing user location:', error);
  }

  dispatchRegionSelectionChanged();
}

/**
 * Get user location with priority: user_profiles (if logged in) > localStorage > null
 */
export async function getUserLocationWithPriority(userId?: string, countryCode?: string): Promise<UserLocation | null> {
  if (userId) {
    try {
      const remoteLocation = await loadLocationFromFirestore(userId, countryCode);
      if (remoteLocation) {
        return remoteLocation;
      }

      const localLocation = getUserLocation(countryCode);
      if (localLocation) {
        syncLocationToFirestore(userId, localLocation).catch((err) => {
          console.error('Background sync failed:', err);
        });
        return localLocation;
      }
    } catch (error) {
      console.error('Error loading location from user_profiles:', error);
    }
  }

  return getUserLocation(countryCode);
}
